<template>
    <div
        class="container-file-component"
        :class="{ 'dragging-on': isDragged }"
        @drop="dropEvent"
        @dragenter="dragEnterService"
        @dragleave="dragLeaveService"
        @dragover="preventDefault"
    >
        <div class="container-file-component__document-side">
            <span
                v-if="
                    hasError && hasError.has && !hasError.has('documentImage')
                "
            >
                {{ $t(headerFile) }}
            </span>
        </div>
        <div>
            <div
                class="container-file-component__upload-section"
                :class="[
                    isDetectedMobileDevice.value && !imageString
                        ? 'change-if-mobile-device'
                        : '',
                ]"
            >
                <label
                    v-if="!isFilled || isUploadError"
                    ref="labelRef"
                    :for="name"
                    class="upload-btn"
                    @click="checkFileUploaded"
                >
                    {{ $t(`${TRANSLATION_KEY}.chooseFile`) }}
                </label>
                <input
                    :id="name"
                    ref="inputRef"
                    type="file"
                    :name="name"
                    @change="updateFile"
                />
            </div>

            <div
                v-if="!isDetectedMobileDevice.value || imageString"
                class="container-file-component__thumbnail-section"
                :style="{ 'margin-left': !file ? '10px' : '0' }"
            >
                <div v-if="!isHEIF">
                    <div
                        v-if="isFilled"
                        class="container-file-component__thumbnail-section-thumbnail"
                    >
                        <img
                            class="normal-picture-element"
                            :src="imageString"
                            alt="picture of document"
                        />
                        <span
                            class="short-info remove-picture"
                            @click="clearImage"
                        >
                            {{ $t(`${TRANSLATION_KEY}.removePhoto`) }}
                        </span>
                    </div>
                </div>
                <div v-else>
                    <div
                        v-if="!isFilled"
                        class="container-file-component__thumbnail-section-drag-here"
                    >
                        <div class="lds-roller">
                            <div />
                            <div />
                            <div />
                            <div />
                            <div />
                            <div />
                            <div />
                            <div />
                        </div>
                    </div>
                    <div
                        v-else
                        class="container-file-component__thumbnail-section-thumbnail"
                    >
                        <img
                            class="heic-picture-element"
                            :src="imageString"
                            alt="picture of document"
                        />
                        <button type="button" @click="clearImage" />
                        <span
                            class="short-info remove-picture"
                            @click="clearImage"
                        >
                            {{ $t(`${TRANSLATION_KEY}.removePhoto`) }}
                        </span>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script lang="ts">
import { defineComponent, ref, onMounted, watch, computed } from 'vue';
import { useStore } from 'vuex';
import { UtilityHelper } from '@/helpers/UtilityHelper';

export default defineComponent({
    name: 'File',
    props: {
        name: {
            type: String,
            required: true,
        },
        headerFile: {
            type: String,
            required: false,
            default: '',
        },
    },
    emits: ['fileTypeValidation', 'update', 'seterror'],
    setup(props, { emit }) {
        const store = useStore();
        const inputRef = ref<HTMLInputElement | null>(null);
        const labelRef = ref<HTMLElement | null>(null);
        const headerFileRef = ref<HTMLElement | null>(null);

        const errors = ref(store.getters.errors);
        const isDetectedMobileDevice = ref(store.state.isDetectedMobileDevice);
        const hasError = computed(() => store.state.errors);

        const isUploadError = ref<boolean>(false);
        const size = ref<number | null>(null);
        const isCleared = ref<boolean>(false);
        const isDragged = ref<boolean>(false);
        const isFilled = ref<boolean>(false);
        const isHEIF = ref<boolean>(false);
        const dragAndDropSupported = ref<boolean>(false);
        const file = ref<File | null>(null);
        const fileExtensions = [
            'jpg',
            'jpeg',
            'png',
            'gif',
            'HEIC',
            'HEIF',
            'avif',
            'pdf',
        ] as string[];
        const imageString = ref<string>('');
        const TRANSLATION_KEY = 'basic.fields.File';

        onMounted(() => {
            const div = document.createElement('div');
            dragAndDropSupported.value =
                'draggable' in div || ('ondragstart' in div && 'ondrop' in div);
        });

        const handlePopup = () => {
            store.state.isPhotoInstructionsOpen =
                !store.state.isPhotoInstructionsOpen;
        };

        const checkFileUploaded = (evt: MouseEvent) => {
            if (!labelRef.value) return;
            if (labelRef.value.classList.contains('green-btn')) {
                evt.preventDefault();
            }
        };

        const dropEvent = (evt: DragEvent) => {
            dragLeaveService(evt);
            const dataTransfer = evt.dataTransfer;

            if (dataTransfer && dataTransfer.files.length > 0) {
                const draggedFile = dataTransfer.files[0];
                const splitArray = draggedFile.name.split('.');
                const fileExtension = splitArray[splitArray.length - 1];

                const isConsistentExtension = fileExtensions.filter(
                    (ext) => ext.toLowerCase() === fileExtension.toLowerCase()
                );

                if (isConsistentExtension.length) {
                    if (
                        isConsistentExtension.includes('HEIF') ||
                        isConsistentExtension.includes('HEIC')
                    ) {
                        isHEIF.value = true;
                        convertImage(draggedFile).then((result: File) => {
                            file.value = result;
                            readImageAsString(file.value);
                        });
                    } else {
                        file.value = validatePhotoExtension(draggedFile);
                        readImageAsString(file.value);
                    }
                } else {
                    emit('fileTypeValidation');
                }
            }
        };

        const clearImage = () => {
            isFilled.value = false;
            isUploadError.value = false;
            isHEIF.value = false;
            imageString.value = '';
            file.value = null;
            size.value = null;
            if (inputRef.value) {
                inputRef.value.value = '';
            }
        };

        const updateFile = () => {
            if (!inputRef.value || !inputRef.value.files?.length) return;

            const uploadedFile = inputRef.value.files[0];
            const splitArray = uploadedFile.name.split('.');
            const fileExtension = splitArray[splitArray.length - 1];

            const isConsistentExtension = fileExtensions.filter(
                (ext) => ext.toLowerCase() === fileExtension.toLowerCase()
            );

            if (isConsistentExtension.length) {
                if (
                    isConsistentExtension.includes('HEIF') ||
                    isConsistentExtension.includes('HEIC')
                ) {
                    isHEIF.value = true;
                    convertImage(uploadedFile).then((result: File) => {
                        file.value = result;
                        readImageAsString(file.value);
                    });
                } else {
                    file.value = validatePhotoExtension(uploadedFile);
                    readImageAsString(file.value);
                }
            } else {
                emit('fileTypeValidation', true);
            }
        };

        const validatePhotoExtension = (someFile: File) => {
            const splittedFileName = someFile.name.split('.');
            const ext =
                splittedFileName[splittedFileName.length - 1].toLowerCase();

            if (ext !== 'jpeg') {
                return someFile;
            } else {
                const blob = new Blob([someFile], { type: 'image/jpeg' });
                let fileName: any = someFile.name.split('.');
                fileName = fileName.slice(0, fileName.length - 1).join('.');
                return new File([blob], `${fileName}.jpg`, {
                    type: 'image/jpeg',
                });
            }
        };

        const dragEnterService = (evt: DragEvent) => {
            preventDefault(evt);
            isDragged.value = true;
        };

        const dragLeaveService = (evt: DragEvent) => {
            preventDefault(evt);
            isDragged.value = false;
        };

        const readImageAsString = (image: File) => {
            const reader = new FileReader();
            reader.readAsDataURL(image);
            reader.addEventListener('load', function () {
                addThumbNail(this.result);
            });
        };

        const addThumbNail = (result: string | ArrayBuffer | null) => {
            imageString.value = typeof result === 'string' ? result : '';
            isFilled.value = Boolean(imageString.value);
            store.state.isLocked = '';
            store.state.errors.delete('documentImage');

            setTimeout(() => {
                store.state.isLoaderText = false;
            }, 200);
        };

        const preventDefault = (evt: DragEvent) => {
            evt.preventDefault();
        };

        const setCompressedImage = (blob: Blob, mimeType: string) => {
            if (!file.value) return;
            file.value = new File([blob], file.value.name, { type: mimeType });
        };

        const compressImage = async (
            x: number,
            y: number,
            callback: (b: Blob, m: string) => void
        ) => {
            if (!file.value) return;

            const getFileSize = (file.value.size / 1024 / 1024).toFixed(2);
            size.value = parseFloat(getFileSize);

            if (size.value >= 7.0) {
                const canvas = document.createElement('canvas');
                canvas.width = x;
                canvas.height = y;

                const ctx = canvas.getContext('2d');
                await UtilityHelper.sleep(1000);

                const imageElement: HTMLImageElement | null =
                    document.querySelector('.normal-picture-element') ||
                    document.querySelector('.heic-picture-element');

                if (!imageElement) return;

                const splittedArr = file.value.name.split('.');
                const extension = splittedArr[splittedArr.length - 1];
                const mimeType =
                    extension.toLowerCase() === 'jpg'
                        ? 'image/jpeg'
                        : `image/${extension.toLowerCase()}`;

                ctx?.drawImage(imageElement, 0, 0, x, y);
                canvas.toBlob(
                    (blob) => {
                        if (blob) {
                            callback(blob, mimeType);
                        }
                    },
                    mimeType,
                    1
                );
            }
        };

        const convertImage = async (uploadedFile: File): Promise<File> => {
            return uploadedFile;
            // const convertedFile = await heic2any({
            //   blob: uploadedFile,
            //   toType: "image/jpeg",
            //   quality: 0.5,
            // });

            // let filename: any = uploadedFile.name.split(".");
            // filename = filename.slice(0, filename.length - 1).join(".");

            // const fileBlob = Array.isArray(convertedFile) ? convertedFile[0] : convertedFile;

            // return new File([fileBlob], filename + ".jpg", {
            //   type: "image/jpeg",
            // });
        };

        watch(
            () => imageString.value,
            async () => {
                if (size.value && size.value >= 7.0) {
                    await UtilityHelper.sleep(2750);
                }
                emit('update', {
                    key: props.name,
                    value: file.value,
                });
            }
        );

        watch(
            () => file.value,
            () => {
                compressImage(1280, 1024, setCompressedImage);
            }
        );

        watch(
            () => errors.value,
            (newVal) => {
                if (newVal && newVal.size) {
                    isUploadError.value = true;
                    emit('seterror', isUploadError.value);
                    clearImage();
                }
            }
        );

        return {
            inputRef,
            labelRef,
            headerFileRef,

            isUploadError,
            size,
            isCleared,
            isDragged,
            isFilled,
            isHEIF,
            dragAndDropSupported,
            file,
            fileExtensions,
            imageString,
            TRANSLATION_KEY,

            errors,
            isDetectedMobileDevice,
            hasError,

            ...props,

            handlePopup,
            checkFileUploaded,
            dropEvent,
            clearImage,
            updateFile,
            validatePhotoExtension,
            dragEnterService,
            dragLeaveService,
            readImageAsString,
            addThumbNail,
            preventDefault,
            setCompressedImage,
            compressImage,
            convertImage,
        };
    },
});
</script>
